import clsx from "clsx";
import { Product } from "@repo/apis/browse-search-v2";
import { StockPriceData } from "@repo/apis/stockprice";
import { ProductSummaries } from "@repo/apis/summaries";
import * as Flex from "@repo/ui/flex";
import * as ProductTile from "@repo/ui/product-tile";
import { SaveForLater } from "@repo/ui/saved-items";
import { useStore } from "@repo/ui/store";
import { prependUrlWithHttps } from "@repo/utils/prepend-url-with-https";
import { removeBrandPrefix } from "@repo/utils/remove-brand-prefix";
import { useAnalytics } from "@/components/analytics/initialise-analytics";
import { ProductLink } from "@/components/product-link";
import { ProductTileColoursBadge } from "@/components/product-tile-colours-badge/product-tile-colours-badge";
import { COLLAGE } from "@/constants";
import { TYPOGRAPHY_BODY3 } from "@/theme/topshop/typography/typography-constants";
import styles from "./product-grid.module.css";

export type EditorialProduct = Product & StockPriceData & ProductSummaries;

export type ProductViewProps = {
  product: EditorialProduct;
};

const ProductView = (props: ProductViewProps) => {
  const { product } = props;

  if (!product) {
    return <></>;
  }

  const {
    countryData: { countryCode: country, defaultCurrency: currency },
  } = useStore();

  const { pushToDataLayer } = useAnalytics();

  const handleSaveForLaterAnalytics = () => {
    if (!product) {
      return;
    }
    const priceLocal = product.price?.current?.value;
    if (priceLocal == null) {
      return;
    }
    const pricePounds = product.price?.xrp?.value ?? priceLocal;
    pushToDataLayer({
      add_to_wishlist: {
        ecommerce: {
          currency: "GBP",
          value: pricePounds,
          localCurrencyCode: currency,
          valueLocalCurrency: priceLocal,
          items: [
            {
              productId: `${id}`,
              productCode: `${product.productCode}`,
              item_name,
              item_brand: product.brandName,
              price: pricePounds,
              priceLocalCurrency: priceLocal,
              quantity: 1,
              itemType: "Product",
            },
          ],
        },
      },
    });
  };

  const { name, brandName, images, id, colourWayId } = product;

  const imageSrc =
    images?.length && images[0]?.url
      ? prependUrlWithHttps(images[0].url)
      : product.imageUrl || "";

  const itemPrice = product.productPrice || product.price;

  const colourId = colourWayId || images[0]?.colourWayId;

  if (!name || !imageSrc || !itemPrice?.current?.text) {
    return <></>;
  }

  const item_name = removeBrandPrefix({
    brand: brandName,
    text: name,
  });

  return (
    <ProductLink productName={name} productId={id} key={id}>
      <Flex.Container column gap="sm" className={styles.gridFeatureItem}>
        <ProductTile.ProductImage
          imageUrl={imageSrc}
          altText={item_name}
          sourceSetProfile={COLLAGE}
          srcSize={240}
        />
        <Flex.Container className={styles.textContainer}>
          <Flex.Item grow={1}>
            <Flex.Container column>
              <ProductTile.Title
                maxLines={1}
                className={clsx(TYPOGRAPHY_BODY3)}
              >
                {item_name}
              </ProductTile.Title>
              <Flex.Container gap="xs" align="center">
                <ProductTile.Price
                  className={clsx(TYPOGRAPHY_BODY3)}
                  data-testid="product-price"
                  priceData={itemPrice}
                  country={country}
                  currency={currency}
                  previousPriceCopy="Previous Price"
                  currentPriceCopy="Current Price"
                />
                <ProductTileColoursBadge
                  product={product}
                  className={styles.coloursBadge}
                />
              </Flex.Container>
            </Flex.Container>
          </Flex.Item>
          <Flex.Item>
            <SaveForLater
              productId={id}
              colourWayId={colourId ?? undefined}
              className={styles.saveForLaterMobile}
              onAdded={handleSaveForLaterAnalytics}
            />
          </Flex.Item>
        </Flex.Container>
      </Flex.Container>
    </ProductLink>
  );
};

export default ProductView;
